import { QueryClient } from '@tanstack/react-query';

/**
 * Client único do React Query, instanciado no módulo (não dentro de um
 * `useState` no App). É isso que permite os services invalidarem cache sem hook
 * (ver `invalidando` abaixo). App é 100% client-side, então não há risco de
 * vazar cache entre usuários.
 */

const MAX_RETRIES = 3;

/**
 * Status HTTP do erro. Os services propagam o `AxiosError` cru, mas lemos
 * `err.status` também para o caso de um erro já normalizado.
 */
function statusDoErro(erro: unknown): number | undefined {
  const e = erro as { response?: { status?: number }; status?: number };
  return e?.response?.status ?? e?.status;
}

/** `Retry-After` (em segundos) que o backend manda no 429, em ms. */
function retryAfterMs(erro: unknown): number | null {
  const headers = (erro as { response?: { headers?: Record<string, string> } })?.response?.headers;
  const valor = headers?.['retry-after'] ?? headers?.['Retry-After'];
  const segundos = Number(valor);
  return valor && Number.isFinite(segundos) ? segundos * 1000 : null;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      // Skincare não é dado em tempo real; refetch a cada alt-tab só gera carga.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        const status = statusDoErro(error);
        if (status === 429) return failureCount < MAX_RETRIES;
        // 4xx não se resolve repetindo (inclui o 401, que o interceptor já trata)
        if (status && status >= 400 && status < 500) return false;
        return failureCount < MAX_RETRIES;
      },
      retryDelay: (failureCount, error) =>
        // honra o Retry-After do backend; senão ~2s, 4s, 8s + jitter (dessincroniza clientes)
        retryAfterMs(error) ?? 2 ** failureCount * 1000 + Math.random() * 1000,
    },
  },
});

/**
 * Invalida chaves após o sucesso de uma mutação e devolve a resposta intacta.
 * Usado nos services (`.then(invalidando(CHAVES_X.todos))`) para que a
 * invalidação viva junto do request — todo call site tem o mesmo efeito
 * desejado, e nenhuma tela precisa lembrar de refazer o fetch.
 */
export const invalidando =
  <T,>(...chaves: readonly (readonly unknown[])[]) =>
  (resposta: T): T => {
    chaves.forEach((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] }));
    return resposta;
  };

/**
 * Invalida o cache inteiro — para a mutação que muda o que todas as telas podem ver (a
 * autorização dos dados de saúde abre ou fecha anamnese, registros, progresso e IA de uma vez).
 */
export const invalidandoTudo = <T,>(resposta: T): T => {
  queryClient.invalidateQueries();
  return resposta;
};

/**
 * Zera o cache na troca de identidade (login / verificação de e-mail / logout).
 * O app navega por `navigate()` nesses fluxos — sem reload, o cache do usuário
 * anterior sobreviveria. Onde o fluxo usa `window.location`, o reload já resolve.
 */
export const limparCacheSessao = (): void => queryClient.clear();
